import React, { useState } from 'react';
import axios from 'axios';
import { getAuth, getIdToken } from 'firebase/auth';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://backend-core-644060845776.us-central1.run.app';

const B2BOnboardingModal = ({ onComplete }) => {
  const [businessName, setBusinessName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!businessName.trim()) {
      setError('Por favor ingresa el nombre de tu empresa.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error("No user authenticated");
      }
      
      const token = await getIdToken(user);
      await axios.post(
        `${BACKEND_URL}/api/b2b/profile`,
        { business_name: businessName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Successfully created profile
      onComplete();
    } catch (err) {
      console.error("Error creating B2B profile:", err);
      setError(
        err.response?.data?.error || 
        'Hubo un problema al crear tu perfil. Por favor intenta de nuevo.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div className="glass-panel" style={{
        padding: '40px',
        maxWidth: '450px',
        width: '90%',
        textAlign: 'center',
        background: 'rgba(15, 23, 42, 0.95)',
        border: '1px solid rgba(79, 70, 229, 0.4)',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'rgba(79, 70, 229, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
          color: 'var(--primary-color)'
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
            <line x1="12" y1="22.08" x2="12" y2="12"/>
          </svg>
        </div>
        
        <h2 style={{ marginBottom: '10px', fontSize: '1.6rem', color: 'white' }}>{t('b2b.onboarding.title')}</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '30px', fontSize: '0.95rem', lineHeight: '1.5' }}>
          {t('b2b.onboarding.subtitle')}
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ textAlign: 'left' }}>
            <label htmlFor="businessName" style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              {t('b2b.onboarding.inputLabel')}
            </label>
            <input
              id="businessName"
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder={t('b2b.onboarding.inputPlaceholder')}
              autoFocus
              style={{
                width: '100%',
                padding: '14px 16px',
                borderRadius: '8px',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid var(--glass-border)',
                color: 'white',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--primary-color)'}
              onBlur={(e) => e.target.style.borderColor = 'var(--glass-border)'}
              disabled={loading}
            />
          </div>

          {error && (
            <div style={{ color: '#EF4444', fontSize: '0.9rem', textAlign: 'left', padding: '10px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '6px', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btn-primary"
            disabled={loading || !businessName.trim()}
            style={{
              padding: '14px',
              fontSize: '1.05rem',
              fontWeight: '600',
              opacity: (loading || !businessName.trim()) ? 0.7 : 1,
              marginTop: '10px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            {loading ? (
              <>
                <span className="spinner" style={{display: 'inline-block', width: '20px', height: '20px', border: '3px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 1s linear infinite'}}></span>
                Procesando...
              </>
            ) : (
              t('b2b.onboarding.submitButton')
            )}
          </button>
        </form>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default B2BOnboardingModal;
